import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import { PERP_VAULT_INTERFACE_ABI } from '../EvmConstants';
import {
  showTransactionToast,
  showTxSuccess,
  showTxError,
} from "../../Components/Toast/TransactionToast";
import { executeWithGasEstimation } from './tradingEvmService';
import { isTestnet, getContractAddresses } from '../../config/network';

/**
 * Return a clean, user-friendly message for common EVM errors.
 */
const getEvmErrorMessage = (error, fallbackPrefix = "Transaction failed") => {
  const msg = (error?.message || error?.toString() || "").toLowerCase();
  if (msg.includes("rejected") || msg.includes("denied") || msg.includes("cancelled") || msg.includes("user rejected")) {
    return "User declined the transaction.";
  }
  if (msg.includes("insufficient funds") || msg.includes("out of gas")) {
    return "Insufficient funds for gas fees.";
  }
  if (msg.includes("nonce")) {
    return "Transaction nonce error. Please try again.";
  }
  if (msg.includes("network") || msg.includes("chain id")) {
    return "Network error. Please check your wallet is connected to Nibiru EVM.";
  }
  return `${fallbackPrefix}: ${error?.shortMessage || error?.message || "Unknown error"}`;
};

/**
 * Check if withdrawal requests are allowed (pre-flight check to avoid gas waste)
 * @returns {Promise<Object>} - { allowed: boolean, message?: string, nextWindow?: Date }
 */
const checkWithdrawalWindowEvm = async () => {
  try {
    // Import the checkWithdrawalAllowed function from VaultQueries
    const { checkWithdrawalAllowed } = await import('../../Pages/Trading/Vaults/VaultQueries');
    
    // Use the existing withdrawal window check logic
    const result = await checkWithdrawalAllowed();
    
    return result;
  } catch (error) {
    console.error("Error checking withdrawal window:", error);
    // If check fails, allow the transaction (fail-safe approach)
    return { allowed: true };
  }
};

/**
 * Read-only provider for Nibiru EVM.
 *
 * Vault *reads* must never depend on `window.ethereum`: the injected wallet may be
 * missing entirely (Web3Auth / Thirdweb / WalletConnect sessions) or connected to a
 * different chain, in which case eth_call hits the wrong network, the Nibiru
 * precompiles are absent, and the balance lookup silently resolves to zero — which is
 * exactly how a confirmed deposit ends up displayed as "no deposits".
 * Writes still go through the wallet; only queries use this provider.
 */
let _evmReadProvider = null;
export const getEvmReadProvider = () => {
  if (_evmReadProvider) return _evmReadProvider;

  const rpcUrl = isTestnet()
    ? (process.env.REACT_APP_TESTNET_EVM_RPC || "https://evm-rpc.testnet-2.nibiru.fi")
    : (process.env.REACT_APP_NIBIRU_EVM_RPC || "https://evm-rpc.nibiru.fi");
  const chainIdHex = isTestnet()
    ? (process.env.REACT_APP_TESTNET_EVM_CHAIN_ID || "0x1AFF")
    : (process.env.REACT_APP_NIBIRU_EVM_CHAIN_ID || "0x1AF4");

  // Pinning the network skips eth_chainId detection on every call.
  _evmReadProvider = new ethers.JsonRpcProvider(rpcUrl, parseInt(chainIdHex, 16), {
    staticNetwork: true,
  });
  return _evmReadProvider;
};

/**
 * Get the ERC20 address for a vault's share token
 * @param {string} vaultAddress - Vault bech32 address
 * @returns {Promise<string>} ERC20 address of vault shares
 */
export const getVaultShareErc20Address = async (vaultAddress) => {
  try {
    console.log("Starting vault share ERC20 address query for vault:", vaultAddress);

    // Read-only: always talk to the Nibiru EVM RPC directly so the lookup works even
    // when no injected wallet is present or the wallet sits on another chain.
    const provider = getEvmReadProvider();
    
    // Use the Wasm precompile to query the vault share denom
    const WASM_PRECOMPILE_ADDRESS = process.env.REACT_APP_NIBIRU_WASM_PRECOMPILE_ADDRESS || "0x0000000000000000000000000000000000000802";
    console.log("Using Wasm precompile address:", WASM_PRECOMPILE_ADDRESS);
    
    // Query the vault share denom
    const wasmQuery = ethers.toUtf8Bytes(JSON.stringify({ get_vault_share_denom: {} }));
    console.log("Constructed Wasm query for vault share denom");
    
    const queryData = ethers.concat([
      ethers.id("query(string,bytes)").slice(0, 10),
      ethers.AbiCoder.defaultAbiCoder().encode(["string", "bytes"], [vaultAddress, wasmQuery])
    ]);

    console.log("Executing Wasm query to get vault share denom");
    const sharesDenomResponse = await provider.call({
      to: WASM_PRECOMPILE_ADDRESS,
      data: queryData
    });

    const sharesDenom = ethers.AbiCoder.defaultAbiCoder().decode(["string"], sharesDenomResponse)[0];
    const cleanSharesDenom = sharesDenom.replace(/"/g, '');
    console.log("Retrieved vault share denom:", cleanSharesDenom);
    
    // Now get the ERC20 address for this denom using FunToken precompile
    const FUNTOKEN_PRECOMPILE_ADDRESS = process.env.REACT_APP_NIBIRU_FUNTOKEN_PRECOMPILE_ADDRESS || "0x0000000000000000000000000000000000000800";
    console.log("Using FunToken precompile address:", FUNTOKEN_PRECOMPILE_ADDRESS);
    
    const getErc20AddressData = ethers.concat([
      ethers.id("getErc20Address(string)").slice(0, 10),
      ethers.AbiCoder.defaultAbiCoder().encode(["string"], [cleanSharesDenom])
    ]);

    console.log("Executing FunToken query to get ERC20 address for denom:", cleanSharesDenom);
    const erc20AddressResponse = await provider.call({
      to: FUNTOKEN_PRECOMPILE_ADDRESS,
      data: getErc20AddressData
    });

    const erc20Address = ethers.AbiCoder.defaultAbiCoder().decode(["address"], erc20AddressResponse)[0];
    console.log("Retrieved vault share ERC20 address:", erc20Address);
    
    return erc20Address;
  } catch (error) {
    console.error("Error getting vault share ERC20 address:", error);
    throw error;
  }
};

/**
 * Get a user's vault-share ERC20 balance in base units (no rounding).
 *
 * Kept separate from the formatted helpers: rounding to 2 decimals before the caller
 * re-scales by 1e6 both loses precision and turns a small-but-real balance into 0,
 * which reads as "no deposit".
 * @param {string} account - User's EVM address
 * @param {string} shareTokenAddress - ERC20 address of the vault share token
 * @returns {Promise<string>} Balance in base units, "0" on failure
 */
export const getVaultShareBalanceBaseUnitsEvm = async (account, shareTokenAddress) => {
  try {
    if (!account || !shareTokenAddress || !shareTokenAddress.startsWith("0x")) return "0";

    const provider = getEvmReadProvider();
    const balance = await provider.call({
      to: shareTokenAddress,
      data: ethers.concat([
        ethers.id("balanceOf(address)").slice(0, 10),
        ethers.AbiCoder.defaultAbiCoder().encode(["address"], [account])
      ])
    });

    if (!balance || balance === "0x") return "0";
    return ethers.AbiCoder.defaultAbiCoder().decode(["uint256"], balance)[0].toString();
  } catch (error) {
    console.error("Error fetching raw vault share balance:", error);
    return "0";
  }
};

/**
 * Get user's vault share balance (ERC20)
 * @param {string} account - User's EVM address
 * @param {string} vaultAddress - Vault bech32 address
 * @returns {Promise<string>} Balance in formatted units
 */
export const getVaultShareBalanceEvm = async (account, vaultAddress) => {
  try {
    console.log("Starting vault share balance query for account:", account, "vault:", vaultAddress);

    const provider = getEvmReadProvider();
    
    // Get the vault share ERC20 address (this should return an EVM address, not bech32)
    const shareTokenAddress = await getVaultShareErc20Address(vaultAddress);
    console.log("Retrieved share token address (EVM format):", shareTokenAddress);
    
    // Check if the returned address is in EVM format
    if (!shareTokenAddress.startsWith('0x')) {
      throw new Error(`Invalid ERC20 token address format: ${shareTokenAddress}. Expected 0x format.`);
    }
    
    // Get balance directly using provider
    console.log("Querying ERC20 balance for account:", account);
    const balance = await provider.call({
      to: shareTokenAddress,
      data: ethers.concat([
        ethers.id("balanceOf(address)").slice(0, 10),
        ethers.AbiCoder.defaultAbiCoder().encode(["address"], [account])
      ])
    });

    const balanceDecoded = ethers.AbiCoder.defaultAbiCoder().decode(["uint256"], balance)[0];
    
    // Fix: Convert string to number for decimals
    const decimals = parseInt(process.env.REACT_APP_USDC_DECIMALS || "6", 10);
    const formattedBalance = Number(ethers.formatUnits(balanceDecoded, decimals));
    
    console.log("ERC20 Vault Share Balance result:", {
      account: account,
      shareTokenAddress: shareTokenAddress,
      rawBalance: balanceDecoded.toString(),
      decimals: decimals,
      formattedBalance: formattedBalance
    });

    return formattedBalance.toFixed(2);
  } catch (error) {
    console.error("Error fetching vault share balance:", error);
    return "0.00";
  }
};

/**
 * Get the ERC20 address for a vault's collateral
 * @param {string} vaultAddress - Vault bech32 address
 * @returns {Promise<string>} ERC20 address
 */
export const getCollateralErc20AddressOfVault = async (vaultAddress) => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    
    if (!contractAddress) {
      throw new Error("Missing contract address configuration");
    }

    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, provider);
    
    const collateralAddress = await contract.getCollateralErc20AddressOfVault(vaultAddress);
    
    return collateralAddress;
  } catch (error) {
    console.error("Error getting collateral ERC20 address:", error);
    throw error;
  }
};

/**
 * Deposit to vault using EVM
 * @param {Object} opts - Deposit options
 * @returns {Promise<Object>} Result object
 */
export const depositToVaultEvm = async (opts) => {
  const {
    vaultAddress,
    depositAmount,
    useErc20Amount = "0",
    collateralAddress,
    sendSharesToEvm = true,
    receiver = null
  } = opts;

  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    // Construct deposit message
    const depositMessage = {
      deposit: {
        receiver: receiver,
        lock_duration: null
      }
    };

    const wasmMsgExecuteBytes = ethers.toUtf8Bytes(JSON.stringify(depositMessage));

    showTransactionToast("Depositing to vault...");

    const tx = await executeWithGasEstimation(contract, 'deposit', [
      wasmMsgExecuteBytes,
      depositAmount,
      useErc20Amount,
      vaultAddress,
      collateralAddress,
      sendSharesToEvm,
    ]);

    const receipt = await tx.wait();

    // showTxSuccess("Deposit successful!");

    return {
      success: true,
      txHash: receipt.hash,
      receipt
    };
  } catch (error) {
    console.error("Error depositing to vault:", error);
    showTxError(getEvmErrorMessage(error, "Deposit failed"));
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Deposit with discount and lock using EVM
 * @param {Object} opts - Deposit options
 * @returns {Promise<Object>} Result object
 */
export const depositWithDiscountAndLockEvm = async (opts) => {
  const {
    vaultAddress,
    depositAmount,
    useErc20Amount = "0",
    collateralAddress,
    lockDuration,
    sendSharesToEvm = true,
    receiver = null
  } = opts;

  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    // The vault contract folded DepositWithDiscountAndLock into Deposit: passing a
    // `lock_duration` is what makes it a locked deposit. The old variant no longer
    // exists on-chain and now fails with "unknown variant".
    // `lock_duration` is a u64 — it must be a JSON number, not a string.
    const depositMessage = {
      deposit: {
        receiver: receiver || null,
        lock_duration: Number(lockDuration)
      }
    };

    console.log("Constructed locked deposit message:", depositMessage);

    const wasmMsgExecuteBytes = ethers.toUtf8Bytes(JSON.stringify(depositMessage));

    console.log("Calling deposit with parameters:", {
      vaultAddress,
      depositAmount,
      useErc20Amount,
      collateralAddress,
      sendSharesToEvm,
      lockDuration
    });

    showTransactionToast("Depositing with lock...");

    const tx = await executeWithGasEstimation(contract, 'deposit', [
      wasmMsgExecuteBytes,
      depositAmount,
      useErc20Amount,
      vaultAddress,
      collateralAddress,
      sendSharesToEvm,
    ]);

    const receipt = await tx.wait();

    // showTxSuccess("Locked deposit successful!");

    return {
      success: true,
      txHash: receipt.hash,
      receipt
    };
  } catch (error) {
    console.error("Error depositing with lock:", error);
    showTxError(getEvmErrorMessage(error, "Locked deposit failed"));

    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Redeem vault shares using EVM
 * @param {Object} opts - Redeem options
 * @returns {Promise<Object>} Result object
 */
export const redeemSharesEvm = async (opts) => {
  const {
    vaultAddress,
    redeemAmount,
    sendCollateralToEvm = true,
    receiver = null
  } = opts;

  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    // Construct redeem message
    const redeemMessage = {
      redeem: {
        shares: redeemAmount,
        depositor: null
      }
    };
    
    const wasmMsgExecuteBytes = ethers.toUtf8Bytes(JSON.stringify(redeemMessage));
    
    showTransactionToast("Redeeming shares...");

    const tx = await executeWithGasEstimation(contract, 'redeem', [
      wasmMsgExecuteBytes,
      vaultAddress,
      redeemAmount,
      sendCollateralToEvm,
    ]);

    const receipt = await tx.wait();

    showTxSuccess("Shares redeemed successfully!");

    return {
      success: true,
      txHash: receipt.hash,
      receipt
    };
  } catch (error) {
    console.error("Error redeeming shares:", error);
    showTxError(getEvmErrorMessage(error, "Redeem failed"));
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Make withdrawal request using EVM
 * @param {Object} opts - Withdrawal options
 * @returns {Promise<Object>} Result object
 */
export const makeWithdrawRequestEvm = async (opts) => {
  const {
    vaultAddress,
    withdrawAmount = "0",
    useErc20Amount = "0"
  } = opts;

  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    console.log("Checking withdrawal window before proceeding...");
    const withdrawalCheck = await checkWithdrawalWindowEvm();
    
    if (!withdrawalCheck.allowed) {
      const nextWindowDate = withdrawalCheck.nextWindow ? 
        withdrawalCheck.nextWindow.toLocaleString() : 
        "Unknown";
      
      const errorMessage = `${withdrawalCheck.message}\nNext window opens at: ${nextWindowDate}`;
      
      console.log("Withdrawal window check failed:", withdrawalCheck);
      // toast.error(errorMessage, { autoClose: 8000 });

      return {
        success: false,
        error: errorMessage,
        code: 'WITHDRAWAL_WINDOW_CLOSED'
      };
    }
    
    console.log("Withdrawal window is open, proceeding with transaction");

    // Create provider without custom network object
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const userAddress = await signer.getAddress();
    
    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }

    const contract = new ethers.Contract(
      contractAddress,
      PERP_VAULT_INTERFACE_ABI,
      signer
    );
    
    // Convert amounts to appropriate format (avoiding BigInt)
    const withdrawAmountNum = parseInt(withdrawAmount) || 0;
    let useErc20AmountNum = parseInt(useErc20Amount) || 0;
    
    // Total shares to request withdrawal for
    let totalShares = withdrawAmountNum + useErc20AmountNum;
    
    console.log("Making withdraw request with parameters:", {
      vaultAddress,
      withdrawAmountNum,
      useErc20AmountNum,
      totalShares,
      userAddress
    });
    
    if (useErc20AmountNum > 0) {
      try {
        // Get the vault share ERC20 address
        const vaultShareErc20Address = await getVaultShareErc20Address(vaultAddress);
        console.log("Vault share ERC20 address:", vaultShareErc20Address);
                
        // Check user's ERC20 vault share balance
        const erc20Contract = new ethers.Contract(
          vaultShareErc20Address,
          ['function balanceOf(address owner) view returns (uint256)'],
          signer
        );
                
        const userErc20Balance = await erc20Contract.balanceOf(userAddress);
        const userErc20BalanceNum = parseInt(userErc20Balance.toString());
                
        console.log("User ERC20 vault share balance check:", {
          userAddress,
          erc20Balance: userErc20BalanceNum,
          requestedAmount: useErc20AmountNum,
          hasEnough: userErc20BalanceNum >= useErc20AmountNum
        });
                
        if (userErc20BalanceNum < useErc20AmountNum) {
          const shortfall = useErc20AmountNum - userErc20BalanceNum;
          console.log("ERC20 vault shares balance check:", {
            required: useErc20AmountNum,
            available: userErc20BalanceNum,
            shortfall: shortfall
          });
                    
          const maxAllowedShortfall = Math.pow(10, (process.env.REACT_APP_USDC_DECIMALS || 6) - 2); // 0.01 tokens in base units
                    
          if (shortfall <= maxAllowedShortfall) {
            console.log(`Small shortfall detected (${shortfall} base units), using available balance instead`);
            // toast.info(`Adjusting withdrawal amount to available balance (${(userErc20BalanceNum / Math.pow(10, process.env.REACT_APP_USDC_DECIMALS || 6)).toFixed(6)} tokens)`);
                        
            useErc20AmountNum = userErc20BalanceNum;
            totalShares = withdrawAmountNum + useErc20AmountNum;
                        
            console.log("Adjusted withdrawal parameters:", {
              adjustedUseErc20Amount: useErc20AmountNum,
              adjustedTotalShares: totalShares
            });
          } else {
            console.error("Insufficient ERC20 vault shares:", {
              required: useErc20AmountNum,
              available: userErc20BalanceNum,
              shortfall: shortfall
            });
                        
            const requiredTokens = (useErc20AmountNum / Math.pow(10, process.env.REACT_APP_USDC_DECIMALS || 6)).toFixed(6);
            const availableTokens = (userErc20BalanceNum / Math.pow(10, process.env.REACT_APP_USDC_DECIMALS || 6)).toFixed(6);
                        
            // toast.error(`Insufficient vault share tokens. You need ${requiredTokens} but only have ${availableTokens} ERC20 vault shares.`);
            return {
              success: false,
              error: `Insufficient ERC20 vault shares. Required: ${requiredTokens}, Available: ${availableTokens}`
            };
          }
        }
                
      } catch (balanceError) {
        console.error("ERC20 balance check failed:", balanceError);
        showTxError(getEvmErrorMessage(balanceError, "Failed to check vault shares"));
        return { success: false, error: balanceError.message };
      }
    }
    
    // Construct the withdrawal request message
    const wasmMsg = {
      make_withdraw_request: {
        auto_redeem: true,
        is_evm_origin: true
      }
    };
    const wasmMsgBytes = ethers.toUtf8Bytes(JSON.stringify(wasmMsg));
    
    showTransactionToast("Making withdrawal request...");

    const tx = await executeWithGasEstimation(contract, 'makeWithdrawRequest', [
      vaultAddress,
      wasmMsgBytes,
      totalShares.toString(),
      useErc20AmountNum.toString(),
    ]);

    const receipt = await tx.wait();

    // showTxSuccess("Withdrawal request submitted successfully!");

    return { success: true, receipt };
  } catch (error) {
    console.error("EVM withdrawal request error:", error);
    showTxError(getEvmErrorMessage(error, "Withdrawal request failed"));
    return { success: false, error: error.message };
  }
};

/**
 * Cancel withdrawal request using EVM
 * @param {Object} opts - Cancel options
 * @returns {Promise<Object>} Result object
 */
export const cancelWithdrawRequestEvm = async (opts) => {
  const { unlockEpoch } = opts;

  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    // Construct cancel message
    const cancelMessage = {
      cancel_withdraw_request: {
        unlock_epoch: unlockEpoch
      }
    };
    
    const wasmMsgExecuteBytes = ethers.toUtf8Bytes(JSON.stringify(cancelMessage));
    
    showTransactionToast("Cancelling withdrawal request...");

    const tx = await executeWithGasEstimation(contract, 'executeVaultSimpleFunctions', [
      wasmMsgExecuteBytes,
      getContractAddresses().vault,
    ]);

    const receipt = await tx.wait();

    showTxSuccess("Withdrawal request cancelled successfully!");

    return {
      success: true,
      txHash: receipt.hash,
      receipt
    };
  } catch (error) {
    console.error("Error cancelling withdrawal request:", error);
    showTxError(getEvmErrorMessage(error, "Cancel failed"));
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Unlock deposit using EVM
 * @param {Object} opts - Unlock options
 * @returns {Promise<Object>} Result object
 */
export const unlockDepositEvm = async (opts) => {
  const { depositId, receiver = null } = opts;

  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    // Construct unlock message
    const unlockMessage = {
      unlock_deposit: {
        deposit_id: depositId,
        receiver: receiver
      }
    };
    
    const wasmMsgExecuteBytes = ethers.toUtf8Bytes(JSON.stringify(unlockMessage));
    
    showTransactionToast("Unlocking deposit...");

    const tx = await executeWithGasEstimation(contract, 'executeVaultSimpleFunctions', [
      wasmMsgExecuteBytes,
      getContractAddresses().vault,
    ]);

    const receipt = await tx.wait();

    showTxSuccess("Deposit unlocked successfully!");

    return {
      success: true,
      txHash: receipt.hash,
      receipt
    };
  } catch (error) {
    console.error("Error unlocking deposit:", error);
    showTxError(getEvmErrorMessage(error, "Unlock failed"));
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Execute simple vault functions (generic function for other vault operations)
 * @param {Object} wasmMessage - The Wasm message object
 * @returns {Promise<Object>} Result object
 */
export const executeSimpleVaultFunctionEvm = async (wasmMessage) => {
  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    const wasmMsgExecuteBytes = ethers.toUtf8Bytes(JSON.stringify(wasmMessage));
    
    showTransactionToast("Processing request...");

    const tx = await executeWithGasEstimation(contract, 'executeVaultSimpleFunctions', [
      wasmMsgExecuteBytes,
      getContractAddresses().vault,
    ]);

    const receipt = await tx.wait();

    showTxSuccess("Request processed successfully!");

    return {
      success: true,
      txHash: receipt.hash,
      receipt
    };
  } catch (error) {
    console.error("Error executing vault function:", error);
    showTxError(getEvmErrorMessage(error, "Failed to execute function"));
    return {
      success: false,
      error: error.message
    };
  }
};